import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ChatListPage.css';

const ChatListPage = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const getToken = () => localStorage.getItem('token');
  
  const getUserRole = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.role;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const fetchChats = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/login');
        return;
      }

      // Super admins should see all tenants' chats
      const role = getUserRole();
      const allTenantsParam = role === 'super_admin' ? '&all_tenants=true' : '';

      const response = await axios.get(
        `${BACKEND_URL}/api/chats/list?limit=50${allTenantsParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setChats(response.data.chats || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching chats:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load chats');
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchChats();
    // Poll for updates every 5 seconds
    const interval = setInterval(fetchChats, 5000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': '#25d366',
      'completed': '#00a884',
      'pending': '#ffb74d',
      'closed': '#8696a0'
    };
    return colors[status] || colors['active'];
  };

  if (loading) {
    return (
      <div className="chat-list-loading">
        <div className="loading-spinner"></div>
        <p>Loading chats...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-list-error">
        <h2>⚠️ Error</h2>
        <p>{error}</p>
        <button onClick={fetchChats}>Retry</button>
      </div>
    );
  }

  return (
    <div className="chat-list-container">
      <div className="chat-list-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1>Case Chats</h1>
      </div>

      <div className="chats-list">
        {chats.length === 0 ? (
          <div className="no-chats">
            <p>No active chats</p>
            <small>Chats will appear here when cases are created</small>
          </div>
        ) : (
          chats.map((chat) => {
            const lastMsg = chat.last_message || {};
            const lastContent = lastMsg.content || 'No messages yet';
            
            return (
              <div
                key={chat.chat_id}
                className="chat-item"
                onClick={() => navigate(`/case-chat/${chat.case_id}`)}
              >
                <div className="chat-item-icon">💬</div>
                
                <div className="chat-item-content">
                  {chat.tenant_name && (
                    <div className="chat-tenant-badge">
                      🏢 {chat.tenant_name}
                    </div>
                  )}
                  
                  <div className="chat-item-header">
                    <div className="chat-title-row">
                      <h3 className="case-number-display">{chat.case_id}</h3>
                      {chat.bank_display_name && (
                        <span className="chat-bank-badge">
                          🏦 {chat.bank_display_name}
                        </span>
                      )}
                    </div>
                    <span 
                      className="chat-status-dot"
                      style={{ backgroundColor: getStatusColor(chat.status) }}
                    />
                  </div>
                  
                  {chat.created_by_email && (
                    <p className="chat-user-info">
                      👤 {chat.created_by_email}
                    </p>
                  )}
                  
                  <p className="chat-last-message">
                    {lastContent.length > 60 
                      ? lastContent.substring(0, 60) + '...' 
                      : lastContent}
                  </p>
                  
                  <div className="chat-item-footer">
                    <span className="chat-participants">
                      {chat.participants?.length || 0} participant{chat.participants?.length !== 1 ? 's' : ''}
                    </span>
                    <span className="chat-time">
                      {formatTime(chat.updated_at)}
                    </span>
                  </div>
                </div>

                <div className="chat-item-arrow">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatListPage;
